import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { createColumnHelper } from '@tanstack/react-table';
import { Calendar } from 'lucide-react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import NoDataBoard from '@/component-library/widgets/NoDataBoard/NoDataBoard';
import Table from '@/component-library/widgets/Table/Table';
import { MemberOverview } from '@/models/AudienceByWorkspace';
import { ChurnRiskHistoryRow } from '@/models/ChurnRisk';

interface MemberChurnHistoryTabProps {
  memberData: MemberOverview | undefined;
}

type ChartPoint = { label: string; riskScore: number; calculatedAt: string };

const MemberChurnHistoryTab: React.FC<MemberChurnHistoryTabProps> = ({ memberData }) => {
  const { t } = useTranslation();
  const rows = memberData?.churnRiskHistory ?? [];

  const chartData = useMemo(() => {
    const sorted = [...rows].sort(
      (a, b) => new Date(a.calculatedAt).getTime() - new Date(b.calculatedAt).getTime(),
    );
    return sorted.map(
      (r): ChartPoint => ({
        calculatedAt: r.calculatedAt,
        riskScore: r.riskScore,
        label: new Date(r.calculatedAt).toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }),
      }),
    );
  }, [rows]);

  const tableRows = useMemo(
    () =>
      [...rows].sort((a, b) => new Date(b.calculatedAt).getTime() - new Date(a.calculatedAt).getTime()),
    [rows],
  );

  const columnHelper = createColumnHelper<ChurnRiskHistoryRow>();

  const tierColor = (tier: string) => {
    const x = tier.toLowerCase();
    if (x === 'critical' || x === 'high') return 'bg-custom-red/10 text-custom-red border-custom-red/20';
    if (x === 'medium') return 'bg-custom-yellow/10 text-custom-yellow border-custom-yellow/20';
    return 'bg-custom-green/10 text-custom-green border-custom-green/20';
  };

  const scoreColor = (s: number) => {
    if (s >= 60) return 'text-custom-red';
    if (s >= 30) return 'text-custom-yellow';
    return 'text-custom-green';
  };

  const columns = [
    columnHelper.accessor('calculatedAt', {
      header: () => (
        <div className="text-custom-black font-medium">
          {t('MemberProfilePage.churnHistoryCalculatedColumn', 'Calculated')}
        </div>
      ),
      cell: (info) => (
        <div className="flex items-center text-sm text-custom-darkSilver">
          <Calendar className="mr-1 h-4 w-4" />
          {new Date(info.getValue()).toLocaleString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>
      ),
    }),
    columnHelper.accessor('riskScore', {
      header: () => (
        <div className="text-custom-black font-medium">
          {t('MemberProfilePage.riskScoreColumnLabel', 'Risk')}
        </div>
      ),
      cell: (info) => (
        <span className={`font-medium ${scoreColor(Number(info.getValue()))}`}>{info.getValue()}%</span>
      ),
    }),
    columnHelper.accessor('riskTier', {
      header: () => (
        <div className="text-custom-black font-medium">
          {t('MemberProfilePage.churnHistoryTierColumn', 'Tier')}
        </div>
      ),
      cell: (info) => {
        const v = (info.getValue() || '—').toString();
        return (
          <span className={`rounded-md border px-2 py-1 text-xs ${tierColor(v)}`}>{v.toLowerCase()}</span>
        );
      },
    }),
    columnHelper.accessor('engagementLevel', {
      header: () => (
        <div className="text-custom-black font-medium">
          {t('MemberProfilePage.churnHistoryEngagementColumn', 'Engagement')}
        </div>
      ),
      cell: (info) => <span className="text-custom-black">{info.getValue() || '—'}</span>,
    }),
    columnHelper.accessor('subscriptionStatus', {
      header: () => (
        <div className="text-custom-black font-medium">
          {t('MemberProfilePage.churnHistorySubscriptionColumn', 'Subscription')}
        </div>
      ),
      cell: (info) => <span className="text-custom-black capitalize">{info.getValue() || '—'}</span>,
    }),
    columnHelper.accessor('eventTypeContribution', {
      header: () => (
        <div className="text-custom-black font-medium">
          {t('MemberProfilePage.churnHistoryEventRiskColumn', 'Event risk Δ')}
        </div>
      ),
      cell: (info) => {
        const v = info.getValue();
        if (v === undefined) return <span className="text-custom-darkSilver">—</span>;
        return <span className="text-custom-black">{v > 0 ? `+${v}` : String(v)}</span>;
      },
    }),
  ];

  if (rows.length === 0) {
    return (
      <NoDataBoard
        description={t(
          'MemberProfilePage.churnHistoryEmpty',
          'No churn history yet. Run “Calculate churn” (or wait for the next calculation) to record snapshots over time.',
        )}
      />
    );
  }

  return (
    <div className="flex flex-col gap-8">
      {chartData.length >= 2 && (
        <div className="w-full">
          <h3 className="mb-3 text-base font-medium text-custom-black">
            {t('MemberProfilePage.churnHistoryChartTitle', 'Risk score over time')}
          </h3>
          <div className="h-[240px] w-full min-w-0 rounded-lg border border-gray-200 bg-white p-4">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} width={36} />
                <Tooltip
                  formatter={(value: number) => [`${value}%`, t('MemberProfilePage.riskScoreColumnLabel', 'Risk')]}
                />
                <Line type="monotone" dataKey="riskScore" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div>
        <h3 className="mb-3 text-base font-medium text-custom-black">
          {t('MemberProfilePage.churnHistoryTableTitle', 'Snapshots')}
        </h3>
        <Table columns={columns} data={tableRows} />
      </div>
    </div>
  );
};

export default MemberChurnHistoryTab;
